/**
 * UI capabilities aligned with backend `permissions/route_permissions` + common UX groupings.
 */

import type { Me } from '../../modules/admin/types';
import { hasPermission } from '../utils/permissions';
import {
  CONTRACT_ACCESS,
  QUEST_ACCESS,
  ADMIN_MANAGE_ROLES,
  ADMIN_MANAGE_USERS,
  RISK_ACCESS,
  INCIDENT_ACCESS,
  BASELINE_SECURITY_ACCESS,
  THREAT_HUNT_ACCESS,
} from './slugs';

/** Submit / Stream / Review questionnaire flows. */
export function canWriteQuestionnaireFlow(me: Me | null): boolean {
  return hasPermission(me, QUEST_ACCESS);
}

export function canReadQuestionnaire(me: Me | null): boolean {
  return hasPermission(me, QUEST_ACCESS);
}

/** Can open Q&A read APIs used by review-style UIs. */
export function canAccessQuestionnaireReviewUi(me: Me | null): boolean {
  return hasPermission(me, QUEST_ACCESS);
}

/** Train Helios card, RAG upload, and training workflows. */
export function canTrainHelios(me: Me | null): boolean {
  return hasPermission(me, QUEST_ACCESS);
}

export function canWriteContract(me: Me | null): boolean {
  return hasPermission(me, CONTRACT_ACCESS);
}

export function canReadContract(me: Me | null): boolean {
  return hasPermission(me, CONTRACT_ACCESS);
}

/** Show Contract Review on home and allow /contract-review routes. */
export function canAccessContractReview(me: Me | null): boolean {
  return hasPermission(me, CONTRACT_ACCESS);
}

/** Show Questionnaire on home when user has questionnaire access. */
export function canAccessQuestionnaire(me: Me | null): boolean {
  return hasPermission(me, QUEST_ACCESS);
}

export function canAccessRiskManagement(me: Me | null): boolean {
  return hasPermission(me, RISK_ACCESS);
}

export function canAccessSecurityIncident(me: Me | null): boolean {
  return hasPermission(me, INCIDENT_ACCESS);
}

export function canAccessThreatHunt(me: Me | null): boolean {
  return hasPermission(me, THREAT_HUNT_ACCESS);
}

export function canAccessAdmin(me: Me | null): boolean {
  return hasPermission(me, ADMIN_MANAGE_ROLES) || hasPermission(me, ADMIN_MANAGE_USERS);
}

export function canManageUsers(me: Me | null): boolean {
  return hasPermission(me, ADMIN_MANAGE_ROLES) || hasPermission(me, ADMIN_MANAGE_USERS);
}

export function canAccessBaselineSecurity(me: Me | null): boolean {
  return hasPermission(me, BASELINE_SECURITY_ACCESS);
}
